/**
 * 🔴 THE MOBILE DECISION, AS A FUNCTION RATHER THAN A BREAKPOINT CLASS.
 *
 * A multi-column board cannot exist on a phone. Or reviews on one and asked about this directly, and
 * the SRS is explicit: **the board shows ONE LANE AT A TIME with a swipe between them** (§7). ⭐ That
 * is a re-think, not a reflow — the obvious implementation, a horizontally-scrolling six-column strip
 * at 390px, is technically responsive and practically unusable.
 *
 * ⭐⭐ It is a function, and the component obeys it, for one reason: a Tailwind `hidden md:flex` is a
 * decision no test can read. This one is asserted at 390 and at 1280, and the component renders the
 * OTHER lanes not at all — they are absent from the DOM, not merely off-screen, so a phone never
 * builds six lanes' worth of cards to show one.
 */

/** Tailwind's `md`. Below it, one lane; at it and above, the full board. */
export const SINGLE_LANE_MAX_WIDTH = 767;

/** Below this a column would be narrower than a card's own minimum and text would wrap to nothing. */
export const MIN_COLUMN_WIDTH = 232;

export type BoardMode = 'single-lane' | 'columns';

export interface BoardLayout {
  mode: BoardMode;
  /** How many columns fit without horizontal scrolling. Informational — the board scrolls anyway. */
  columnsThatFit: number;
  /** True when the reader must swipe or use the tabs to see another lane. */
  laneAtATime: boolean;
}

export function boardLayout(viewportWidth: number): BoardLayout {
  const width = Number.isFinite(viewportWidth) ? Math.max(0, viewportWidth) : 0;
  const mode: BoardMode = width <= SINGLE_LANE_MAX_WIDTH ? 'single-lane' : 'columns';
  return {
    mode,
    columnsThatFit: Math.max(1, Math.floor(width / MIN_COLUMN_WIDTH)),
    laneAtATime: mode === 'single-lane',
  };
}

/** Wraps at both ends, so a swipe past the last lane returns to the first rather than dead-ending. */
export function nextLaneIndex(current: number, count: number, direction: 1 | -1): number {
  if (count <= 0) return 0;
  return (current + direction + count) % count;
}
